/**
 * Hybrid logical clock. Serialised as a fixed-width string so plain string
 * comparison gives the causal order: `<ms 13>-<counter 5>-<node>`.
 * Used for per-field last-writer-wins when records sync between devices.
 */
export interface Hlc {
  ms: number;
  counter: number;
  node: string;
}

const MAX_DRIFT_MS = 60_000;

export function formatHlc(h: Hlc): string {
  return `${h.ms.toString().padStart(13, '0')}-${h.counter.toString(36).padStart(5, '0')}-${h.node}`;
}

export function parseHlc(s: string): Hlc {
  const [ms, counter, ...node] = s.split('-');
  if (!ms || !counter || node.length === 0) throw new Error(`Invalid HLC: ${s}`);
  return { ms: Number(ms), counter: parseInt(counter, 36), node: node.join('-') };
}

export class HlcClock {
  private last: Hlc;

  constructor(
    node: string,
    private readonly wallClock: () => number = Date.now,
  ) {
    this.last = { ms: 0, counter: 0, node };
  }

  /** Timestamp for a local change. */
  now(): string {
    const wall = this.wallClock();
    if (wall > this.last.ms) this.last = { ...this.last, ms: wall, counter: 0 };
    else this.last = { ...this.last, counter: this.last.counter + 1 };
    return formatHlc(this.last);
  }

  /** Merge a timestamp seen on a remote change, so later local stamps sort after it. */
  receive(remote: string): void {
    const r = parseHlc(remote);
    const wall = this.wallClock();
    if (r.ms - wall > MAX_DRIFT_MS) throw new Error('Remote clock too far ahead');
    const ms = Math.max(wall, this.last.ms, r.ms);
    let counter = 0;
    if (ms === this.last.ms && ms === r.ms) counter = Math.max(this.last.counter, r.counter) + 1;
    else if (ms === this.last.ms) counter = this.last.counter + 1;
    else if (ms === r.ms) counter = r.counter + 1;
    this.last = { ms, counter, node: this.last.node };
  }
}
